import { useEffect, useState } from 'react'
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import type { VideoListQuery, VideoQuickCounts, VideoStage, VideoStageCounts } from '@ayub/shared'
import { VIDEOS_PAGE_SIZE, fetchVideos } from './api'
import type { VideoListResponse } from './api'
import { sortVideosByUrgency } from './urgency'

/** "Hammasi" chipi — haqiqiy `VideoStage` emas, filtrlanmagan holat. */
export const STAGE_ALL = 'ALL' as const
export type StageFilter = typeof STAGE_ALL | VideoStage

export type DueFilter = NonNullable<VideoListQuery['due']>

/** Qidiruv maydonini debounce qiladi — har bosilgan tugma uchun so'rov yubormaslik uchun. */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])

  return debounced
}

interface UseVideoListParams {
  stage: StageFilter
  due: DueFilter | undefined
  q: string
  page: number
}

/** List ko'rinishi uchun sahifalangan so'rov. */
export function useVideoList({ stage, due, q, page }: UseVideoListParams) {
  const query = useQuery<VideoListResponse, Error>({
    queryKey: ['videos', 'list', stage, due, q, page],
    queryFn: () =>
      fetchVideos({
        stage: stage === STAGE_ALL ? undefined : stage,
        due,
        q: q.length > 0 ? q : undefined,
        page,
        pageSize: VIDEOS_PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.isError ? query.error : null,
    refetch: query.refetch,
  }
}

/** Kanban ustun sarlavhalari uchun har bir bosqichdagi umumiy son — filtrlardan mustaqil. */
export function useVideoStageCounts() {
  const query = useQuery<VideoStageCounts, Error>({
    queryKey: ['videos', 'stageCounts'],
    queryFn: async () => (await fetchVideos({ page: 1, pageSize: 1 })).stageCounts,
    placeholderData: keepPreviousData,
  })

  return {
    counts: query.data,
    isLoading: query.isLoading,
    error: query.isError ? query.error : null,
  }
}

/** Tez filtr chiplaridagi sonlar (`docs/03-app-flow.md` "Tez filtrlar") — filtrlardan mustaqil. */
export function useVideoQuickCounts() {
  const query = useQuery<VideoQuickCounts, Error>({
    queryKey: ['videos', 'quickCounts'],
    queryFn: async () => (await fetchVideos({ page: 1, pageSize: 1 })).quickCounts,
    placeholderData: keepPreviousData,
  })

  return {
    counts: query.data,
    isLoading: query.isLoading,
    error: query.isError ? query.error : null,
  }
}

interface UseVideoColumnResult {
  items: VideoListResponse['items']
  total: number
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  hasMore: boolean
  loadMore: () => void
}

/**
 * Kanban ustuni uchun ma'lumot — `docs/04-ui-ux.md`: "Ustun ichida vertikal skroll, 50 tadan
 * keyin 'yana yuklash'". `useInfiniteQuery` bilan sahifama-sahifa yuklanadi (`components/
 * bloggers/hooks.ts`dagi `useBloggerColumn` bilan bir xil sabab: server `pageSize`ni cheklaydi).
 *
 * Kelgan sahifalar `sortVideosByUrgency()` bilan qayta tartiblanadi — server allaqachon
 * saralab yuborishi mumkin, lekin mijoz tomonida ham kafolatlash arzon va xato holatlarda
 * (masalan sahifalar orasida) tartibni saqlaydi.
 */
export function useVideoColumn(
  stage: VideoStage,
  q: string,
  due: DueFilter | undefined,
): UseVideoColumnResult {
  const query = useInfiniteQuery<VideoListResponse, Error>({
    queryKey: ['videos', 'kanban', stage, q, due],
    queryFn: ({ pageParam }) =>
      fetchVideos({
        stage,
        due,
        q: q.length > 0 ? q : undefined,
        page: pageParam as number,
        pageSize: VIDEOS_PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
  })

  const rawItems = query.data ? query.data.pages.flatMap((p) => p.items) : []
  const items = sortVideosByUrgency(rawItems)
  const total = query.data?.pages[0]?.total ?? 0

  return {
    items,
    total,
    isLoading: query.isLoading,
    isFetching: query.isFetching || query.isFetchingNextPage,
    error: query.isError ? query.error : null,
    hasMore: query.hasNextPage ?? false,
    loadMore: () => void query.fetchNextPage(),
  }
}
